// ==========================================
// FormContentManager.jsx (material = meta 방식)
// ==========================================

import { useEffect, useRef, useState } from "react";

import * as designMeta from "../../../api/design/meta";
import * as qualityMeta from "../../../api/quality/meta";
import * as approvalMeta from "../../../api/approval/meta";
import * as materialMeta from "../../../api/material/meta"; // ⭐ material = meta 방식
import * as safetyMeta from "../../../api/safety";
import * as constMeta from "../../../api/const";

import { uploadDesignFile } from "../../../api/design/file";

import AttachmentBox from "../papers/AttachmentBox";
import ApprovalLine from "../../approval/ApprovalLine";
import DocSummaryCard from "./DocSummary";
import PageHeader from "../ui/PageHeader";

import { useNavigate } from "react-router-dom";
import { updateDocPersons } from "../../../api/papers";
import {
  saveApprovalLinesForDocument,
  addApprovalLine,
} from "../../../api/approval";

// --------------------------------------------------------------------
// 모듈별 저장 함수 매핑
// --------------------------------------------------------------------
const saveFunctions = {
  design: {
    create: designMeta.createMeta,
    update: designMeta.updateMeta,
  },
  quality: {
    create: qualityMeta.createQualityMeta,
    update: qualityMeta.updateQualityMeta,
  },
  approval: {
    create: approvalMeta.createMeta,
    update: null,
  },
  material: {
    create: materialMeta.createMeta,
    update: materialMeta.updateMeta,
  },
  safety: {
    create: safetyMeta.saveSafetyDoc,
    update: safetyMeta.saveSafetyDoc,
  },
  const: {
    create: constMeta.createConstMeta,
    update: constMeta.updateConstMeta,
  },
};

// --------------------------------------------------------------------
function ensureProject(p) {
  return (
    p ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode")
  );
}

// --------------------------------------------------------------------
function goBoard({ module, project, redirectTo, docType }) {
  const safeDocType =
    typeof docType === "string" && docType.trim() ? docType.trim() : "";
  const url =
    redirectTo ||
    (module === "design"
      ? `/project/${project}/design/list`
      : module === "quality"
        ? `/project/${project}/quality/qualityboard`
        : module === "safety"
          ? `/project/${project}/safety/board${safeDocType ? `/${safeDocType}` : ""
          }`
          : `/project/${project}`);

  window.location.replace(url);
}

// --------------------------------------------------------------------
export default function FormContentManager({
  params,
  docName,
  docDescribe,
  getBody,
  children,
  onChangeId,
  onSaved,
  redirectTo,
  summaryConfig,
  summaryComponent,
  reviewer,
  requestPeople,
  headerExtraButtons, // 💡 문서 입력 헤더 오른쪽에 붙일 추가 버튼
  hideBackButton = false, // 💡 모바일에서 "목록으로" 버튼 숨김
  hideSaveButton = false, // 💡 모바일에서 상단 "문서 저장" 버튼 숨김
  mobileFullScreen = false, // 💡 모바일 전체화면 모드 - 기본 레이아웃 숨김
}) {
  const attachmentRef = useRef();
  const approvalRef = useRef();
  const navigate = useNavigate();

  console.log(reviewer);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [approvalMode, setApprovalMode] = useState("normal");

  const [values, setValues] = useState(null);

  const norm = {
    module: (params?.module || "").toLowerCase(),
    project: ensureProject(params?.project),
    form_type: (params?.formType || params?.doc_type || "").toUpperCase(),
    dns: params?.dns ?? null,
  };

  // -------------------------------------------------------------
  // 최초 getBody 읽기
  // -------------------------------------------------------------
  useEffect(() => {
    const b = getBody?.() || {};
    setValues(b.values || b);
  }, []);

  // -------------------------------------------------------------
  // 주문서 자동 결재선 세팅
  // -------------------------------------------------------------
  useEffect(() => {
    if (!values) return;

    if (values.doc_type === "order" || values.doc_type === "ordercomplete") {
      if (!values.confirmer) return;

      approvalRef.current?.setLines?.([
        {
          id: Date.now(),
          approver_id: values.confirmer,
          approver_name: values.confirmer_name,
          order: 1,
          approval_type: "sequential",
          approval_category: "approval",
        },
      ]);
    }
  }, [values]);

  // -------------------------------------------------------------
  // DNS 뽑기 유틸
  // -------------------------------------------------------------
  const extractDNS = (dns, result) => {
    return (
      dns ||
      result?.dns ||
      result?.doc_dns ||
      result?.data?.dns ||
      result?.data?.doc_dns
    );
  };

  // -------------------------------------------------------------
  // 저장 처리
  // -------------------------------------------------------------
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError("");

    try {
      const body = getBody?.() || {};
      const values = body.values || body;
      const meta = values.meta || {};
      const project = norm.project;

      if (!project) throw new Error("프로젝트 정보가 없습니다.");

      let dns = norm.dns;
      let result;

      const fns = saveFunctions[norm.module];
      if (!fns) throw new Error(`지원되지 않는 모듈입니다.`);

      // -------------------------------------------------------------
      // DESIGN
      // -------------------------------------------------------------
      if (norm.module === "design") {
        result = dns
          ? await fns.update(dns, values, project)
          : await fns.create(values, project);

        dns = extractDNS(dns, result);
        if (!dns) throw new Error("DNS 생성 실패(design)");

        // DCN 파일 업로드
        if (norm.form_type === "DCN" && Array.isArray(meta.details)) {
          for (let i = 0; i < meta.details.length; i++) {
            const d = meta.details[i];
            if (d.file && !d.fileUrl) {
              const uploaded = await uploadDesignFile(d.file, dns, {
                index: i + 1,
              });
              d.fileUrl = uploaded.url;
              d.fileName = uploaded.file_name;
              d.fileId = uploaded.id;
              d.file = null;
            }
          }
        }
      }

      // -------------------------------------------------------------
      // CONST DAILY (별도 API)
      // -------------------------------------------------------------
      else if (norm.module === "const" && norm.form_type === "DAILY") {
        result = dns
          ? await constMeta.updateWorkReport(dns, values.meta)
          : await constMeta.createWorkReport(values.meta);

        dns = dns || result?.dns;
        if (!dns) throw new Error("DNS 생성 실패(DAILY)");
      }

      // -------------------------------------------------------------
      // MATERIAL (meta 방식)
      // -------------------------------------------------------------
      else if (norm.module === "material") {
        result = dns
          ? await fns.update(dns, values, project)
          : await fns.create(values, project);

        dns = extractDNS(dns, result);
        if (!dns) throw new Error(`DNS 생성 실패(material)`);
      }

      // -------------------------------------------------------------
      // 나머지 모듈 (quality / safety / const 일반 문서)
      // -------------------------------------------------------------
      else {
        console.log(`💾 [FormContentManager] ${norm.module} 저장:`, {
          dns,
          willCreate: !dns,
          willUpdate: !!dns,
        });

        result = dns ? await fns.update(dns, values) : await fns.create(values);

        console.log(`💾 [FormContentManager] ${norm.module} 저장 결과:`, result);

        dns = extractDNS(dns, result);
        if (!dns) throw new Error(`DNS 생성 실패(${norm.module})`);
      }

      // -------------------------------------------------------------
      // 첨부 저장
      // -------------------------------------------------------------
      await attachmentRef.current?.save?.(dns);

      // -------------------------------------------------------------
      // 결재 저장
      // -------------------------------------------------------------
      if (approvalMode === "approval") {
        await approvalRef.current?.save?.(dns);
      }

      // ORDER 자동결재 처리
      if (values.doc_type === "order" || values.doc_type === "ordercomplete") {
        if (values.confirmer) {
          await new Promise((r) => setTimeout(r, 0));
          await approvalRef.current?.save?.(dns);
        }
      }

      // reviewer 자동 결재선 등록
      if (reviewer?.length > 0) {
        for (let i = 0; i < reviewer.length; i++) {
          await addApprovalLine(
            {
              doc_dns: dns,
              approver_id: reviewer[i].id,
              order: i + 1,
              refresh: i === 0,
            },
            norm.project
          );
        }
      }

      // 담당자 저장
      if (requestPeople?.length > 0) {
        await updateDocPersons(
          dns,
          requestPeople.map((p) => p.id)
        );
      }

      onChangeId?.(dns);
      onSaved?.({ dns });

      goBoard({
        module: norm.module,
        project,
        redirectTo,
        docType: values?.doc_type, // ✅ report|check|scar|riskcheck|incident
      });
      alert("문서 저장 완료");
    } catch (e) {
      console.error(e);
      setError(e?.message || "저장 중 오류 발생");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------
     렌더링
  --------------------------------------------------- */
  // 💡 mobileFullScreen 모드: 기본 레이아웃 숨기고 children만 렌더링
  if (mobileFullScreen) {
    return (
      <>
        {children}
        {/* 숨겨진 AttachmentBox와 ApprovalLine - 저장 시 사용 */}
        <div style={{ display: "none" }}>
          <AttachmentBox ref={attachmentRef} dns={norm.dns} />
          {approvalMode === "approval" && <ApprovalLine ref={approvalRef} />}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="mb-6">
        <PageHeader showBack>{docName} 작성</PageHeader>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className={`col-span-12 ${summaryComponent || summaryConfig ? "lg:col-span-8" : ""} space-y-4`}>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">{docDescribe}</p>

            <div className="flex items-center gap-2">
              {headerExtraButtons}

              {/* 💡 hideSaveButton이 true면 상단 저장 버튼 숨김 (모바일 하단바에서 처리) */}
              {!hideSaveButton && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-create"
                >
                  {saving ? "저장 중…" : "문서 저장"}
                </button>
              )}
            </div>
          </div>

          {/* 문서 유형 - design/safety만 표시 */}
          {/* {["approval"].includes(norm.module) && (
            // <div className="flex items-center gap-2 mt-3 border rounded-lg p-3 bg-white">
            //   <span className="text-sm mr-1">문서 유형</span>

            //   <button
            //     onClick={() => setApprovalMode("normal")}
            //     className={`px-3 py-1.5 rounded-lg border text-sm ${
            //       approvalMode === "normal"
            //         ? "bg-gray-900 text-white"
            //         : "bg-white"
            //     }`}
            //   >
            //     일반
            //   </button>

            //   <button
            //     onClick={() => setApprovalMode("approval")}
            //     className={`px-3 py-1.5 rounded-lg border text-sm ${
            //       approvalMode === "approval"
            //         ? "bg-gray-900 text-white"
            //         : "bg-white"
            //     }`}
            //   >
            //     결재
            //   </button>
            // </div>
            <ApprovalLine ref={approvalRef} />
          )} */}

          {["approval"].includes(norm.module) && (
            <div className="mb-4">
              <ApprovalLine ref={approvalRef} />
            </div>
          )}

          <div className="border rounded-lg p-6 bg-white">{children}</div>

          {/* 첨부 */}
          <div className="border rounded-lg p-4 bg-white">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="text-sm mr-1 mt-1">첨부 자료</span>

                <div className="flex-1">
                  {norm.dns ? (
                    <AttachmentBox ref={attachmentRef} dns={norm.dns} />
                  ) : (
                    <AttachmentBox ref={attachmentRef} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 요약 */}
        <div className="col-span-12 lg:col-span-4">
          {summaryComponent ? (
            summaryComponent
          ) : summaryConfig ? (
            <DocSummaryCard {...summaryConfig} />
          ) : null}
        </div>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}
    </div>
  );
}
